"use client";



import type { ReactNode } from "react";

import {

  Dialog,

  DialogContent,

  DialogDescription,

  DialogTitle,

} from "@/shared/components/ui/dialog";

import { RefreshCw } from "lucide-react";



export type PopupType =

  | "alreadyApplied"

  | "success"

  | "error"

  | "warning"

  | "info"

  | "deleteSuccess"

  | "delete";



interface AlreadyAppliedDialogProps {

  open: boolean;

  message: string;

  onOpenChange: (open: boolean) => void;

  onViewApplicationStatus: () => void;



  type?: PopupType;

  title?: string;

  buttonText?: string;



  secondaryButtonText?: string;

  onSecondaryAction?: () => void;

  /** Optional custom content rendered between the message and actions. */
  content?: ReactNode;

  /** Disables the primary action when required by the caller. */
  primaryButtonDisabled?: boolean;
}



/* =========================================================

   COMMON SVG PROPS

========================================================= */



const SVG_PROPS = {

  viewBox: "0 0 220 160",

  width: 175,

  height: 125,

  fill: "none",

  xmlns: "http://www.w3.org/2000/svg",

};



/* =========================================================

   SUCCESS ILLUSTRATION

   Reused by:

   - AlreadyAppliedDialog

   - SuccessStep

========================================================= */



export function SuccessIllustration() {

  return (

    <svg

      {...SVG_PROPS}

      className="origin-center motion-safe:animate-[pulse_2s_ease-in-out_infinite]"

    >

      {/* Background */}

      <path

        d="M58 126C42 126 31 115 31 101C31 87 43 76 58 76C62 61 76 51 92 51C111 51 126 64 129 82C132 80 136 79 140 79C153 79 163 89 163 102C163 115 153 126 140 126H58Z"

        fill="#EEF4FF"

      />



      {/* Confetti */}

      <rect

        x="38"

        y="43"

        width="8"

        height="18"

        rx="2"

        transform="rotate(-24 38 43)"

        fill="#60A5FA"

        className="motion-safe:animate-bounce"

        style={{

          transformOrigin: "center",

          animationDuration: "2.4s",

        }}

      />



      <rect

        x="168"

        y="42"

        width="8"

        height="17"

        rx="2"

        transform="rotate(24 168 42)"

        fill="#86EFAC"

        className="motion-safe:animate-bounce"

        style={{

          transformOrigin: "center",

          animationDuration: "2.6s",

          animationDelay: "300ms",

        }}

      />



      {/* Floating dots */}

      <circle

        cx="32"

        cy="69"

        r="4"

        fill="#22C55E"

        className="motion-safe:animate-pulse"

      />



      <circle

        cx="190"

        cy="61"

        r="4"

        fill="#F97316"

        className="motion-safe:animate-pulse"

        style={{

          animationDelay: "300ms",

        }}

      />



      {/* Sparkles */}

      <path

        d="M58 32L61 39L68 42L61 45L58 52L55 45L48 42L55 39L58 32Z"

        fill="#FBBF24"

        className="motion-safe:animate-pulse"

      />



      <path

        d="M171 65L174 71L180 74L174 77L171 83L168 77L162 74L168 71L171 65Z"

        fill="#60A5FA"

        className="motion-safe:animate-pulse"

        style={{

          animationDelay: "500ms",

        }}

      />



      {/* Left paper */}

      <g

        className="motion-safe:animate-bounce"

        style={{

          transformOrigin: "center",

          animationDuration: "2.2s",

        }}

      >

        <rect

          x="48"

          y="57"

          width="22"

          height="28"

          rx="3"

          transform="rotate(-17 48 57)"

          fill="white"

          stroke="#D6E4FF"

          strokeWidth="2"

        />



        <path

          d="M54 66H65"

          stroke="#93C5FD"

          strokeWidth="2"

          strokeLinecap="round"

        />



        <path

          d="M53 72H62"

          stroke="#CBD5E1"

          strokeWidth="2"

          strokeLinecap="round"

        />

      </g>



      {/* Right paper */}

      <g

        className="motion-safe:animate-bounce"

        style={{

          transformOrigin: "center",

          animationDuration: "2.2s",

          animationDelay: "400ms",

        }}

      >

        <rect

          x="151"

          y="55"

          width="22"

          height="28"

          rx="3"

          transform="rotate(16 151 55)"

          fill="white"

          stroke="#D6E4FF"

          strokeWidth="2"

        />



        <path

          d="M156 64H167"

          stroke="#86EFAC"

          strokeWidth="2"

          strokeLinecap="round"

        />



        <path

          d="M157 70H165"

          stroke="#CBD5E1"

          strokeWidth="2"

          strokeLinecap="round"

        />

      </g>



      {/* Person body */}

      <path

        d="M83 118C84 94 96 80 110 80C124 80 136 94 137 118H83Z"

        fill="#2563EB"

      />



      {/* Left arm */}

      <g

        className="motion-safe:animate-bounce"

        style={{

          transformOrigin: "91px 91px",

          animationDuration: "1.8s",

        }}

      >

        <path

          d="M91 91L70 63"

          stroke="#2563EB"

          strokeWidth="13"

          strokeLinecap="round"

        />



        <circle

          cx="69"

          cy="61"

          r="7"

          fill="#F4A261"

        />

      </g>



      {/* Right arm */}

      <g

        className="motion-safe:animate-bounce"

        style={{

          transformOrigin: "129px 91px",

          animationDuration: "1.8s",

          animationDelay: "150ms",

        }}

      >

        <path

          d="M129 91L150 61"

          stroke="#2563EB"

          strokeWidth="13"

          strokeLinecap="round"

        />



        <circle

          cx="151"

          cy="59"

          r="7"

          fill="#F4A261"

        />

      </g>



      {/* Neck */}

      <rect

        x="104"

        y="70"

        width="12"

        height="16"

        rx="5"

        fill="#F4A261"

      />



      {/* Face */}

      <circle

        cx="110"

        cy="57"

        r="22"

        fill="#F4A261"

      />



      {/* Hair */}

      <path

        d="M88 56C87 39 96 28 111 28C127 28 136 40 133 57C128 49 122 45 113 45C103 45 96 49 88 56Z"

        fill="#1F2937"

      />



      {/* Eyes */}

      <circle

        cx="103"

        cy="57"

        r="1.8"

        fill="#374151"

      />



      <circle

        cx="117"

        cy="57"

        r="1.8"

        fill="#374151"

      />



      {/* Smile */}

      <path

        d="M104 65C108 69 113 69 117 65"

        stroke="#374151"

        strokeWidth="2"

        strokeLinecap="round"

      />

    </svg>

  );

}



/* =========================================================

   ERROR ILLUSTRATION

========================================================= */



function ErrorIllustration() {

  return (

    <svg

      {...SVG_PROPS}

      className="origin-center motion-safe:animate-pulse"

    >

      <path

        d="M58 126C42 126 31 115 31 101C31 87 43 76 58 76C62 61 76 51 92 51C111 51 126 64 129 82C132 80 136 79 140 79C153 79 163 89 163 102C163 115 153 126 140 126H58Z"

        fill="#FFF1F2"

      />



      <circle

        cx="48"

        cy="55"

        r="5"

        fill="#FCA5A5"

        className="motion-safe:animate-bounce"

      />



      <circle

        cx="172"

        cy="54"

        r="5"

        fill="#FB7185"

        className="motion-safe:animate-bounce"

        style={{

          animationDelay: "200ms",

        }}

      />



      <circle

        cx="110"

        cy="68"

        r="32"

        fill="#FEE2E2"

      />



      <path

        d="M97 55L123 81"

        stroke="#EF4444"

        strokeWidth="7"

        strokeLinecap="round"

      />



      <path

        d="M123 55L97 81"

        stroke="#EF4444"

        strokeWidth="7"

        strokeLinecap="round"

      />



      <path

        d="M78 119C80 96 92 84 110 84C128 84 140 96 142 119H78Z"

        fill="#EF4444"

      />



      <path

        d="M89 94L68 78"

        stroke="#EF4444"

        strokeWidth="12"

        strokeLinecap="round"

      />



      <path

        d="M131 94L152 78"

        stroke="#EF4444"

        strokeWidth="12"

        strokeLinecap="round"

      />



      <circle

        cx="67"

        cy="77"

        r="6"

        fill="#F4A261"

      />



      <circle

        cx="153"

        cy="77"

        r="6"

        fill="#F4A261"

      />

    </svg>

  );

}



/* =========================================================

   WARNING ILLUSTRATION

========================================================= */



function WarningIllustration() {

  return (

    <svg

      {...SVG_PROPS}

      className="origin-center motion-safe:animate-pulse"

    >

      <path

        d="M58 126C42 126 31 115 31 101C31 87 43 76 58 76C62 61 76 51 92 51C111 51 126 64 129 82C132 80 136 79 140 79C153 79 163 89 163 102C163 115 153 126 140 126H58Z"

        fill="#FFF7ED"

      />



      <path

        d="M110 37L145 101H75L110 37Z"

        fill="#FEF3C7"

        stroke="#F59E0B"

        strokeWidth="4"

        strokeLinejoin="round"

        className="motion-safe:animate-bounce"

      />



      <path

        d="M110 59V78"

        stroke="#F59E0B"

        strokeWidth="6"

        strokeLinecap="round"

      />



      <circle

        cx="110"

        cy="89"

        r="4"

        fill="#F59E0B"

      />



      <circle

        cx="53"

        cy="60"

        r="5"

        fill="#FBBF24"

        className="motion-safe:animate-pulse"

      />



      <circle

        cx="168"

        cy="62"

        r="5"

        fill="#F97316"

        className="motion-safe:animate-pulse"

        style={{

          animationDelay: "300ms",

        }}

      />

    </svg>

  );

}



/* =========================================================

   INFO ILLUSTRATION

========================================================= */



function InfoIllustration() {

  return (

    <svg

      {...SVG_PROPS}

      className="origin-center motion-safe:animate-pulse"

    >

      <path

        d="M58 126C42 126 31 115 31 101C31 87 43 76 58 76C62 61 76 51 92 51C111 51 126 64 129 82C132 80 136 79 140 79C153 79 163 89 163 102C163 115 153 126 140 126H58Z"

        fill="#EFF6FF"

      />



      <circle

        cx="110"

        cy="69"

        r="32"

        fill="#DBEAFE"

        className="motion-safe:animate-bounce"

      />



      <circle

        cx="110"

        cy="56"

        r="4"

        fill="#2563EB"

      />



      <path

        d="M110 71V91"

        stroke="#2563EB"

        strokeWidth="6"

        strokeLinecap="round"

      />



      <circle

        cx="55"

        cy="59"

        r="5"

        fill="#60A5FA"

        className="motion-safe:animate-pulse"

      />



      <circle

        cx="165"

        cy="55"

        r="5"

        fill="#93C5FD"

        className="motion-safe:animate-pulse"

        style={{

          animationDelay: "300ms",

        }}

      />

    </svg>

  );

}



/* =========================================================

   DELETE ILLUSTRATION

========================================================= */



function DeleteIllustration() {

  return (

    <svg

      {...SVG_PROPS}

      className="origin-center motion-safe:animate-[pulse_2s_ease-in-out_infinite]"

    >

      {/* Background */}

      <path

        d="M58 126C42 126 31 115 31 101C31 87 43 76 58 76C62 61 76 51 92 51C111 51 126 64 129 82C132 80 136 79 140 79C153 79 163 89 163 102C163 115 153 126 140 126H58Z"

        fill="#FFF1F2"

      />



      {/* Floating dots */}

      <circle

        cx="40"

        cy="61"

        r="4"

        fill="#F87171"

        className="motion-safe:animate-bounce"

      />



      <circle

        cx="181"

        cy="60"

        r="4"

        fill="#FB7185"

        className="motion-safe:animate-bounce"

        style={{

          animationDelay: "250ms",

        }}

      />



      {/* Left sparkle */}

      <path

        d="M54 79L57 86L64 89L57 92L54 99L51 92L44 89L51 86L54 79Z"

        fill="#F87171"

        className="motion-safe:animate-pulse"

      />



      {/* Right sparkle */}

      <path

        d="M169 83L172 89L178 92L172 95L169 101L166 95L160 92L166 89L169 83Z"

        fill="#FB7185"

        className="motion-safe:animate-pulse"

        style={{

          animationDelay: "300ms",

        }}

      />



      {/* Trash container */}

      <g

        className="motion-safe:animate-bounce"

        style={{

          transformOrigin: "110px 87px",

          animationDuration: "1.8s",

        }}

      >

        {/* Lid */}

        <rect

          x="78"

          y="57"

          width="64"

          height="10"

          rx="4"

          fill="#EF4444"

        />



        {/* Handle */}

        <rect

          x="97"

          y="48"

          width="26"

          height="10"

          rx="4"

          fill="#DC2626"

        />



        {/* Body */}

        <path

          d="M84 67H136L131 115C130.5 120 126.5 123 121 123H99C93.5 123 89.5 120 89 115L84 67Z"

          fill="#F87171"

        />



        {/* Highlight */}

        <path

          d="M94 73H126"

          stroke="#FCA5A5"

          strokeWidth="3"

          strokeLinecap="round"

        />



        {/* Lines */}

        <path

          d="M101 83V109"

          stroke="white"

          strokeWidth="5"

          strokeLinecap="round"

        />



        <path

          d="M119 83V109"

          stroke="white"

          strokeWidth="5"

          strokeLinecap="round"

        />

      </g>

    </svg>

  );

}



/* =========================================================

   DELETE SUCCESS ILLUSTRATION

========================================================= */



function DeleteSuccessIllustration() {

  return (

    <svg

      {...SVG_PROPS}

      className="origin-center motion-safe:animate-[pulse_2.2s_ease-in-out_infinite]"

    >

      {/* Soft background */}

      <path

        d="M58 126C42 126 31 115 31 101C31 87 43 76 58 76C62 61 76 51 92 51C111 51 126 64 129 82C132 80 136 79 140 79C153 79 163 89 163 102C163 115 153 126 140 126H58Z"

        fill="#EFF6FF"

      />



      {/* Floating success dots */}

      <circle

        cx="42"

        cy="61"

        r="4"

        fill="#60A5FA"

        className="motion-safe:animate-bounce"

      />



      <circle

        cx="178"

        cy="59"

        r="4"

        fill="#34D399"

        className="motion-safe:animate-bounce"

        style={{

          animationDelay: "250ms",

        }}

      />



      {/* Sparkles */}

      <path

        d="M58 78L61 85L68 88L61 91L58 98L55 91L48 88L55 85L58 78Z"

        fill="#60A5FA"

        className="motion-safe:animate-pulse"

      />



      <path

        d="M166 78L169 85L176 88L169 91L166 98L163 91L156 88L163 85L166 78Z"

        fill="#34D399"

        className="motion-safe:animate-pulse"

        style={{

          animationDelay: "300ms",

        }}

      />



      {/* Deleted document */}

      <g

        className="motion-safe:animate-bounce"

        style={{

          transformOrigin: "92px 91px",

          animationDuration: "1.8s",

        }}

      >

        <rect

          x="67"

          y="55"

          width="47"

          height="59"

          rx="6"

          fill="white"

          stroke="#BFDBFE"

          strokeWidth="3"

        />



        <path

          d="M78 72H103"

          stroke="#93C5FD"

          strokeWidth="4"

          strokeLinecap="round"

        />



        <path

          d="M78 84H98"

          stroke="#CBD5E1"

          strokeWidth="4"

          strokeLinecap="round"

        />



        <path

          d="M78 96H94"

          stroke="#CBD5E1"

          strokeWidth="4"

          strokeLinecap="round"

        />



        {/* Delete mark */}

        <path

          d="M82 105L99 88"

          stroke="#F87171"

          strokeWidth="4"

          strokeLinecap="round"

        />



        <path

          d="M99 105L82 88"

          stroke="#F87171"

          strokeWidth="4"

          strokeLinecap="round"

        />

      </g>



      {/* Success check circle */}

      <g

        className="motion-safe:animate-bounce"

        style={{

          transformOrigin: "142px 101px",

          animationDuration: "1.5s",

          animationDelay: "200ms",

        }}

      >

        <circle

          cx="142"

          cy="101"

          r="25"

          fill="#DCFCE7"

          stroke="#22C55E"

          strokeWidth="3"

        />



        <path

          d="M130 101L138 109L155 91"

          stroke="#16A34A"

          strokeWidth="5"

          strokeLinecap="round"

          strokeLinejoin="round"

        />

      </g>

    </svg>

  );

}



/* =========================================================

   MAIN DIALOG

========================================================= */



export default function AlreadyAppliedDialog({
  open,
  message,
  onOpenChange,
  onViewApplicationStatus,
  type = "alreadyApplied",
  title,
  buttonText = "View Application Status",
  secondaryButtonText,
  onSecondaryAction,
  content,
  primaryButtonDisabled,
}: AlreadyAppliedDialogProps) {
  const popupTitle =
    title ||
    {
      alreadyApplied: "Application Already Submitted",
      success: "Congratulations!",
      deleteSuccess: "Team Member Deleted",
      error: "Oops!",
      warning: "Please Check",
      info: "Information",
      delete: "Delete Team Member?",
    }[type];

  const renderIllustration = () => {
    switch (type) {
      case "success":
        return <SuccessIllustration />;
      case "error":
        return <ErrorIllustration />;
      case "warning":
        return <WarningIllustration />;
      case "info":
        return <InfoIllustration />;
      case "deleteSuccess":
        return <DeleteSuccessIllustration />;
      case "delete":
        return <DeleteIllustration />;
      case "alreadyApplied":
      default:
        return <WarningIllustration />;
    }
  };

  const isDeletePopup = type === "delete";
  const isCompactPopup = Boolean(content);
  const hasSecondaryAction = Boolean(onSecondaryAction);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className={
          isCompactPopup
            ? "w-[calc(100vw-32px)] max-w-md gap-2 rounded-lg border bg-background p-4 shadow-lg sm:p-5"
            : "w-[90vw] max-w-[380px] overflow-hidden rounded-[14px] border border-white bg-white p-0 shadow-[0_18px_45px_rgba(0,0,0,0.18)]"
        }
      >
        <div
          className={
            isCompactPopup
              ? "relative"
              : "relative px-7 pb-7 pt-5 text-center"
          }
        >
          {!isCompactPopup && (
            <div className="mx-auto mb-2 flex h-[125px] w-[175px] items-center justify-center">
              {renderIllustration()}
            </div>
          )}

          {isCompactPopup && (
            <div className="mx-auto mb-2 flex h-10 w-10 items-center justify-center rounded-full bg-blue-100 text-blue-600 ring-1 ring-blue-200">
              <RefreshCw className="h-5 w-5" aria-hidden="true" />
            </div>
          )}

          <DialogTitle
            className={
              isCompactPopup
                ? "text-center text-base font-semibold leading-5 text-foreground"
                : "text-[18px] font-semibold leading-6 text-gray-900"
            }
          >
            {popupTitle}
          </DialogTitle>

          <DialogDescription
            className={
              isCompactPopup
                ? "mx-auto mt-1.5 max-w-sm text-center text-[13px] leading-5 text-muted-foreground"
                : "mx-auto mt-2 max-w-[300px] text-[12px] leading-[18px] text-gray-600"
            }
          >
            {message}
          </DialogDescription>

          {content && (
            <div className="mt-3 w-full text-left">{content}</div>
          )}

          <div
            className={
              isCompactPopup
                ? "mt-4 flex items-center justify-end gap-2"
                : hasSecondaryAction
                  ? "mt-5 flex items-center justify-center gap-2.5"
                  : "mt-5 flex items-center justify-center"
            }
          >
            {onSecondaryAction && (
              <button
                type="button"
                onClick={onSecondaryAction}
                className="inline-flex h-9 min-w-[100px] items-center justify-center rounded-md border border-border bg-background px-4 text-sm font-medium text-foreground shadow-sm transition hover:bg-accent hover:text-accent-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-1"
              >
                {secondaryButtonText || "Cancel"}
              </button>
            )}

            <button
              type="button"
              onClick={onViewApplicationStatus}
              disabled={primaryButtonDisabled}
              className={
                isCompactPopup
                  ? "inline-flex h-9 min-w-0 items-center justify-center rounded-md bg-primary px-4 text-sm font-medium text-primary-foreground shadow-sm transition hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none disabled:opacity-50"
                  : `
                      inline-flex h-9 items-center justify-center rounded-[5px] px-5 text-[12px] font-medium text-white shadow-[0_3px_8px_rgba(52,120,246,0.25)] transition focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50
                      ${
                        isDeletePopup
                          ? "min-w-[100px] bg-[#EF4444] shadow-[0_3px_8px_rgba(239,68,68,0.22)] hover:bg-[#DC2626] focus:ring-[#EF4444]"
                          : "min-w-[155px] bg-[#3478F6] hover:bg-[#2868DE] focus:ring-[#3478F6]"
                      }
                    `
              }
            >
              {buttonText}
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
